<div 
    class="fixed inset-0 flex items-center justify-center bg-main-background bg-opacity-75 z-30"
    (click)="$event.preventDefault(); handleClose();"
>
    <div class="w-full max-w-2xl bg-white p-8 rounded-md shadow-lg" (click)="$event.stopPropagation()">
        <!-- HEADER -->
        <div class="w-full flex flex-row items-start justify-between gap-8 pb-4 border-b border-gray-200">
            <div class="flex flex-col gap-1">
                <h2 class="oswald-font text-2xl font-medium">Upload Case to Assess</h2>
                <p class="text-sm text-gray-text">Upload the case file below to continue with this Behavioral Threat Assessment.</p>
            </div>
            <button type="button" class="p-1" (click)="$event.preventDefault(); handleClose();">
                <lucide-icon [img]="icons.X" [size]="20"></lucide-icon>
            </button>
        </div>

        <!-- BODY -->
        <div class="w-full flex flex-col gap-4 py-6 border-b border-gray-200">
            <div class="w-full flex flex-row items-start gap-4">
                <label class="w-44 shrink-0 pt-2.5 text-sm">Case ID:</label>
                <div class="w-full flex flex-col gap-1">
                    <div class="w-full flex items-center justify-between bg-gray-100 border border-gray-300 rounded p-2.5">
                        <span class="font-mono">{{ caseId }}</span>
                        <lucide-icon [img]="icons.Lock" [size]="16" class="text-gray-text"></lucide-icon>
                    </div>
                    <span class="text-xs text-gray-text">Generated automatically. It can't be changed.</span>
                </div>
            </div>

            <div class="w-full flex flex-row items-start gap-4">
                <label class="w-44 shrink-0 pt-2.5 text-sm">Case file:</label>
                <div class="w-full flex flex-col gap-2">
                    <div
                        class="w-full flex flex-col items-center justify-center gap-2 p-6 rounded border border-dashed"
                        [ngClass]="{
                            'border-nside-orange-400 bg-orange-50': dragging,
                            'border-gray-400 bg-gray-50': !dragging
                        }"
                        (dragover)="handleDragOver($event)"
                        (dragleave)="handleDragLeave($event)"
                        (drop)="handleDrop($event)"
                    >
                        <lucide-icon [img]="icons.Upload" [size]="28" class="text-nside-orange-400"></lucide-icon>
                        <span class="text-sm text-gray-text">Drag a file here, or</span>
                        <button
                            type="button"
                            class="flex items-center gap-2 px-4 py-1.5 rounded-md border border-nside-orange-400 text-nside-orange-400 hover:bg-orange-50 oswald-font"
                            (click)="$event.preventDefault(); fileInput.click();"
                        >
                            <lucide-icon [img]="icons.Upload" [size]="16"></lucide-icon>
                            Upload file
                        </button>
                        <input #fileInput type="file" class="hidden" multiple [accept]="accept" (change)="handleFileInput($event)">
                        <span *ngIf="!selectedFiles.length" class="text-xs text-gray-text">No file selected</span>
                        <span class="text-xs text-gray-text">PDF, JPG, PNG, CSV or XLS, up to {{ maxTotalSizeMb }} MB in total</span>
                    </div>

                    <ul *ngIf="selectedFiles.length" class="w-full flex flex-col gap-1">
                        <li
                            *ngFor="let file of selectedFiles; let i = index"
                            class="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded bg-gray-50 border border-gray-200 text-sm"
                        >
                            <span class="flex items-center gap-2 min-w-0">
                                <lucide-icon [img]="icons.FileText" [size]="16" class="shrink-0"></lucide-icon>
                                <span class="truncate">{{ file.name }}</span>
                                <span class="shrink-0 text-xs text-gray-text">{{ formatSize(file.size) }}</span>
                            </span>
                            <button type="button" class="p-0.5" title="Remove" (click)="$event.preventDefault(); removeFile(i);">
                                <lucide-icon [img]="icons.X" [size]="14"></lucide-icon>
                            </button>
                        </li>
                    </ul>

                    <div
                        *ngIf="selectedFiles.length"
                        class="w-full text-end text-xs"
                        [ngClass]="{ 'text-red-500': isOverSizeLimit, 'text-gray-text': !isOverSizeLimit }"
                    >
                        Total {{ formatSize(totalSize) }} of {{ maxTotalSizeMb }} MB
                        <span *ngIf="isOverSizeLimit">. Remove some files to continue.</span>
                    </div>
                </div>
            </div>

            <div class="w-full flex flex-row items-start gap-4">
                <label class="w-44 shrink-0 text-sm">Does this case require a Full Team Threat Assessment?</label>
                <div class="w-full flex flex-row items-center gap-6 pt-1">
                    <label class="flex items-center gap-2 cursor-pointer">
                        <input type="radio" [formControl]="fullTeamRequired" [value]="1" class="text-nside-orange-400 focus:ring-nside-orange-300">
                        Yes
                    </label>
                    <label class="flex items-center gap-2 cursor-pointer">
                        <input type="radio" [formControl]="fullTeamRequired" [value]="0" class="text-nside-orange-400 focus:ring-nside-orange-300">
                        No
                    </label>
                </div>
            </div>

            <div class="w-full flex flex-col gap-1 text-xs">
                <span class="text-gray-text">The case will be closed after you save. No review is required.</span>
                <span *ngIf="errorMessage" class="italic text-red-500">{{ errorMessage }}</span>
            </div>
        </div>

        <!-- FOOTER -->
        <div class="w-full flex flex-row gap-3 items-center justify-end pt-6">
            <button
                type="button"
                class="px-4 py-1.5 w-28 rounded-md border border-nside-gray-400 oswald-font"
                (click)="$event.preventDefault(); handleClose();"
            >
                Close
            </button>
            <button
                type="button"
                class="px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
                [ngClass]="{
                    'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !canSave,
                    'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:from-nside-orange-100 hover:to-nside-orange-200': canSave
                }"
                [disabled]="!canSave"
                (click)="$event.preventDefault(); saveAndClose();"
            >
                Save and Close
            </button>
        </div>
    </div>

    <div *ngIf="loading">
        <spinner></spinner>
    </div>
</div>
